import {
  FileImageOutlined,
  FilePdfOutlined,
  FormatPainterOutlined,
  MessageOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import AttachmentEditor from '@components/AttachmentEditor'
import BookletUploader from '@components/BookletUploader'
import FormCard from '@components/FormCard'
import Program from '@components/membership/program/Program'
import Page from '@components/Page/Page'
import {
  updateProgramLogo,
  updateProgramPerksPdf,
} from '@services/membership/ProgramService'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getProgramWithSlug,
  selectSelectedProgram,
} from '@store/membership/ProgramReducer'
import { SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { requiredRule } from '@utils/rules'
import { Button, Card, Col, Flex, Form, Row, Space, Typography } from 'antd'
import { useEffect, useState } from 'react'

const { Title } = Typography

const DesignBrandingTab = () => {
  const program = useAppSelector(selectSelectedProgram())
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!program) return
    form.setFieldsValue({
      ...program,
      logo: program.logo?.url
        ? [
            {
              name: program.logo.name ?? 'image.jpeg',
              url: program.logo.url,
            },
          ]
        : [],
    })
  }, [program])

  if (!program) {
    return <></>
  }

  const onLogoSubmitted = (values: any) => {
    setIsLoading(true)
    const value = values.logo[0].originFileObj
    updateProgramLogo(program, value)
      .then(() => {
        form.resetFields()
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <Page>
      <Row gutter={[32, 32]}>
        <Col span={12}>
          <Form
            form={form}
            layout="vertical"
            name="attachment-editor"
            onFinish={onLogoSubmitted}
          >
            <Flex vertical className="w-full" gap={8}>
              <Space className="w-full space-between">
                <Title level={5} className="m-0">
                  <FileImageOutlined /> Logo
                </Title>
                <Space>
                  <Button
                    style={{ border: 0 }}
                    type="primary"
                    disabled={isLoading}
                    htmlType="submit"
                  >
                    Save
                  </Button>
                </Space>
              </Space>

              <Card>
                <AttachmentEditor
                  formItem={{ name: 'logo', rules: [requiredRule()] }}
                  dragger={{ fileTypes: ['picture'], multiple: false }}
                  image={{ width: 600, height: 148, aspect: 150 / 37 }}
                  noStyle
                />
              </Card>
            </Flex>
          </Form>
        </Col>
        <Col span={12}>
          <Flex vertical className="w-full" gap={8}>
            <Space className="w-full space-between">
              <Title level={5} className="m-0">
                <VideoCameraOutlined /> Video
              </Title>
            </Space>
            <Card>
              <Program.Video id={program.id} />
            </Card>
          </Flex>
        </Col>
        <Col span={24}>
          <BookletUploader
            title={
              <>
                <FilePdfOutlined /> Benefits booklet
              </>
            }
            description="This document will be presented to members in certain transactional emails or in the Benefits tab of the app if you do not use Pléthore's built-in benefits system."
            current={{ fr: program.fr?.perksPdf, en: program.en?.perksPdf }}
            onSubmit={(files) =>
              updateProgramPerksPdf(program, files).then(() =>
                dispatch(getProgramWithSlug(program.slug))
              )
            }
          />
        </Col>
        <Col span={24}>
          <FormCard
            title="Color"
            entity={Entity.membershipProgram}
            action={SBRMAction.Update}
            sections={['color']}
            icon={<FormatPainterOutlined />}
          />
        </Col>
        <Col span={24}>
          <FormCard
            title="Success"
            entity={Entity.membershipProgram}
            action={SBRMAction.Update}
            sections={['success']}
            icon={<MessageOutlined />}
          />
        </Col>
      </Row>
    </Page>
  )
}

export default DesignBrandingTab
